import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Modal } from '../../../Components'
import { useAuth } from '../../../Provider/AuthProvider'
import { FC, invalidateQuery, useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import { createNewProcess, getCorrectState } from './Functions'
import { Icon } from './Icons'
import { ModalEscludiNora } from './ModalEscludiNora'
import { ModalSegnalazione } from './ModalSegnalazione'

const language = cap.language.state || 'IT'
const t = translations[language]

const navigateToConversation = (currentId, delta) => {
  const currentIdNum = parseInt(currentId, 10)
  if (isNaN(currentIdNum)) return
  const newId = currentIdNum + delta
  if (newId > 0) window.location.href = `/operatore/${newId}`
}

// Probes whether an adjacent conversation exists by hitting the same endpoint
// `useCQuery(['conversation', id])` uses. `enabled` skips the request when
// the candidate id is out of range. We don't retry on missing.
const useConversationExists = (id) => {
  const enabled = Number.isFinite(id) && id > 0
  const { isSuccess } = useQuery({
    queryKey: ['conversation', id],
    queryFn: () =>
      FC.service('info').get('getConversationOperatore', { query: { conversationId: id } }),
    enabled,
    retry: false,
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })
  return enabled && isSuccess
}

export const TopBar = ({ stateRef }) => {
  const { conversationId } = useParams()
  const { data: conversation = {} } = useCQuery(['conversation', conversationId])
  const { user: { user = {} } = {} } = useAuth()
  const navigate = useNavigate()
  const isAdmin = user?.role === 'admin'
  const [modalSegnalazione, setModalSegnalazione] = useState(false)
  const [modalEscludiNora, setModalEscludiNora] = useState(false)
  const [callSpinnerEscludiNora, setCallSpinnerEscludiNora] = useState(false)

  const idNum = parseInt(conversationId, 10)
  const prevExists = useConversationExists(idNum - 1)
  const nextExists = useConversationExists(idNum + 1)

  const excludeNoraFromConversation = async () => {
    setCallSpinnerEscludiNora(true)
    try {
      const { atlanteId, username } = user
      const newState = {
        ...getCorrectState(stateRef?.current),
        noraIsIncluded: false,
        logNoraIsExcluded: { atlanteId, username, date: new Date() },
        idUtenteAdv: username
      }
      await createNewProcess(newState, conversationId, 'closeConversationExcludeNora')
      showToast('success', t.gfEsclusioneNora, t.gfOperazioneSuccesso)
      invalidateQuery(['conversation', 'conversations'])
    } catch (e) {
      showToast('error', t.errore, e?.message || t.gfErroreConfermaForm)
    } finally {
      setCallSpinnerEscludiNora(false)
      setModalEscludiNora(false)
    }
  }

  const createTest = async () => {
    try {
      const messages = conversation?.messages || []
      const firstUserMessage =
        messages.find((m) => m.sender === 'traveller')?.message || messages[0]?.message
      const res = await FC.service('autoTests').create({
        messages: [{ user: 'User', message: firstUserMessage }],
        correctState: conversation?.lastProcessStep?.finalState
      })
      if (res) showToast('success', t.gfTestCreato, t.gfTestCreatoSuccesso)
    } catch (e) {
      showToast('error', t.errore, e?.message || t.gfErroreCreazioneTest)
    }
  }

  const showExclude = conversation?.noraIsIncluded && !stateRef?.current?.errorDescriptionToOperator

  return (
    <>
      {/* SUB BAR — page title + nav + page-level actions.
          The global TopMenu already provides app branding, language and theme
          toggle, so we don't render a second top bar here. */}
      <div className="gf-subbar">
        <div className="gf-subbar__left">
          <div className="gf-page-title">{t.gfNuovaConversazione}</div>
          {isAdmin && conversationId && (
            <div className="gf-convo-nav">
              <button
                type="button"
                title={t.gfConversazionePrecedente}
                onClick={() => navigateToConversation(conversationId, -1)}
                disabled={!prevExists}
              >
                <Icon name="chev_left" size={14} />
              </button>
              <div className="gf-convo-nav__label">
                <span className="gf-lbl-full">{t.gfConversazione} </span>
                <b>#{conversationId}</b>
              </div>
              <button
                type="button"
                title={t.gfConversazioneSuccessiva}
                onClick={() => navigateToConversation(conversationId, 1)}
                disabled={!nextExists}
              >
                <Icon name="chev_right" size={14} />
              </button>
            </div>
          )}
        </div>
        <div className="gf-subbar__right">
          <button type="button" className="gf-btn" onClick={() => navigate('/operatore/list')}>
            <Icon name="chev_left" size={13} />
            <span className="gf-lbl">{t.tornaIndietro}</span>
          </button>
          {isAdmin && (
            <>
              <button
                type="button"
                className="gf-btn"
                onClick={() => navigate('/conversations/' + conversationId)}
                title={t.gfVaiAProcessi}
              >
                <Icon name="grid" size={13} />
                <span className="gf-lbl">{t.gfProcessi}</span>
              </button>
              <button type="button" className="gf-btn" onClick={createTest} title={t.gfCreaTest}>
                <Icon name="bolt" size={13} />
                <span className="gf-lbl">{t.gfCreaTest}</span>
              </button>
            </>
          )}
          {!!user?.environment?.enableReport && (
            <button
              type="button"
              className="gf-btn gf-btn--icon"
              title={t.gfInviaSegnalazione}
              onClick={() => setModalSegnalazione(true)}
            >
              <Icon name="flag" size={14} />
            </button>
          )}
          {showExclude && (
            <button
              type="button"
              className="gf-btn gf-btn--danger gf-btn--icon"
              title={t.escludiNora}
              onClick={() => setModalEscludiNora(true)}
            >
              <Icon name="ban" size={14} />
            </button>
          )}
        </div>
      </div>

      <Modal
        visible={modalSegnalazione}
        header={t.gfInviaSegnalazione}
        className="gf-dialog-modal"
        onHide={() => setModalSegnalazione(false)}
        style={{ width: 'min(560px, 92vw)' }}
        contentStyle={{ padding: 0 }}
      >
        <ModalSegnalazione onHide={() => setModalSegnalazione(false)} />
      </Modal>
      <Modal
        visible={modalEscludiNora}
        header={t.escludiNora}
        className="gf-dialog-modal gf-dialog-modal--danger"
        onHide={() => setModalEscludiNora(false)}
        style={{ width: 'min(480px, 92vw)' }}
        contentStyle={{ padding: 0 }}
      >
        <ModalEscludiNora
          callSpinnerEscludiNora={callSpinnerEscludiNora}
          onConfirm={excludeNoraFromConversation}
          onHide={() => setModalEscludiNora(false)}
        />
      </Modal>
    </>
  )
}
